import type { Dashboard, TenantAccess } from '@konyaxl/shared';
import { useCallback, useEffect, useState } from 'react';
import { QrDialog } from '../components/QrDialog.js';
import { ApiRequestError, api } from '../core/api.js';
import { bugunYerel } from '../core/tarih.js';

interface Props {
  tenant: TenantAccess;
  onBack: () => void;
}

interface HataliKayit {
  id: string;
  codeA: string | null;
  codeB: string | null;
  createdAt: string;
  createdByName: string;
}

const bugun = bugunYerel;

/**
 * Gösterge paneli.
 *
 * Kategoriye göre öne çıkan sayı değişiyor: doğrulama yapan kategorilerde
 * (Brisa) "hatalı okuma", eşleştirme yapanlarda (Rabi) "benzersiz eşleşme".
 * Aynı özet iki soruyu da cevaplıyor.
 */
export function DashboardPage({ tenant, onBack }: Props) {
  const [tarih, setTarih] = useState(bugun());
  const [veri, setVeri] = useState<Dashboard | null>(null);
  const [hatalilar, setHatalilar] = useState<HataliKayit[] | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [secili, setSecili] = useState<HataliKayit | null>(null);

  const dogrulamaVar = tenant.matchRule.type !== 'none';

  const yukle = useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    try {
      setVeri(await api.get<Dashboard>(`/${tenant.slug}/dashboard?date=${tarih}`));
    } catch (err) {
      setHata(err instanceof ApiRequestError ? err.message : 'Panel yüklenemedi');
    } finally {
      setYukleniyor(false);
    }
  }, [tenant.slug, tarih]);

  useEffect(() => {
    void yukle();
  }, [yukle]);

  // Tarih değişince açık hata listesi başka güne ait kalmasın.
  useEffect(() => {
    setHatalilar(null);
  }, [tarih]);

  async function hatalariGoster() {
    try {
      const res = await api.get<{ items: HataliKayit[] }>(
        `/${tenant.slug}/invalid?date=${tarih}`,
      );
      setHatalilar(res.items);
    } catch {
      setHata('Hatalı kayıtlar yüklenemedi');
    }
  }

  const enYogunSaat = veri?.byHour.reduce(
    (a, b) => (b.count > (a?.count ?? 0) ? b : a),
    veri.byHour[0],
  );

  return (
    <>
      <header className="ust-bar">
        <button className="btn-geri" onClick={onBack} aria-label="Geri">
          ‹
        </button>
        <h1>{tenant.name} — Gösterge paneli</h1>
        <div className="ust-bar-sag">
          <input
            type="date"
            className="tarih-kutu koyu"
            value={tarih}
            max={bugun()}
            onChange={(e) => setTarih(e.target.value)}
          />
        </div>
      </header>

      <main className="icerik">
        {hata && <div className="hata-kutu">{hata}</div>}

        {yukleniyor && !veri ? (
          <p className="sonuc-bilgi">Yükleniyor…</p>
        ) : veri && veri.total === 0 ? (
          <div className="bos-durum">Bu tarihte kayıt yok</div>
        ) : (
          veri && (
            <>
              <div className="ozet-kartlar">
                <div className="ozet-kart">
                  <span className="ozet-sayi">{veri.total}</span>
                  <span className="ozet-etiket">Toplam okuma</span>
                </div>

                {dogrulamaVar ? (
                  <>
                    <div className="ozet-kart yesil">
                      <span className="ozet-sayi">{veri.valid}</span>
                      <span className="ozet-etiket">Doğru eşleşme</span>
                    </div>
                    <button
                      className={`ozet-kart ${veri.invalid > 0 ? 'kirmizi' : ''}`}
                      onClick={() => void hatalariGoster()}
                      disabled={veri.invalid === 0}
                    >
                      <span className="ozet-sayi">{veri.invalid}</span>
                      <span className="ozet-etiket">
                        Hatalı{veri.invalid > 0 ? ' · göster' : ''}
                      </span>
                    </button>
                  </>
                ) : (
                  <div className="ozet-kart yesil">
                    <span className="ozet-sayi">{veri.uniqueCodeA}</span>
                    <span className="ozet-etiket">Benzersiz eşleşme</span>
                  </div>
                )}

                {/* Referans listesiyle çalışan kategorilerde üçüncü bir durum
                    var: karşılığı listede bulunmayan kayıt. Hatayla aynı
                    kutuya konsaydı kaç gerçek hata yapıldığı görünmezdi. */}
                {tenant.matchRule.type === 'reference' && (
                  <div className={`ozet-kart ${veri.notInList > 0 ? 'turuncu' : ''}`}>
                    <span className="ozet-sayi">{veri.notInList}</span>
                    <span className="ozet-etiket">Listede yok</span>
                  </div>
                )}

                <div className="ozet-kart">
                  <span className="ozet-sayi">{veri.uniqueCodeA}</span>
                  <span className="ozet-etiket">
                    Benzersiz {tenant.scanRules.a.label.toLowerCase()}
                  </span>
                </div>
              </div>

              {dogrulamaVar && veri.total > 0 && (
                <div className="oran-cubugu">
                  <div
                    className="oran-dogru"
                    style={{ width: `${(veri.valid / veri.total) * 100}%` }}
                    title={`${veri.valid} doğru`}
                  />
                  <div
                    className="oran-hatali"
                    style={{ width: `${(veri.invalid / veri.total) * 100}%` }}
                    title={`${veri.invalid} hatalı`}
                  />
                </div>
              )}

              <div className="panel-izgara">
                <section className="bolum">
                  <h2 className="bolum-baslik">Kullanıcı başına</h2>
                  <table className="kayit-tablo">
                    <tbody>
                      {veri.byUser.map((k) => (
                        <tr key={k.name}>
                          <td>{k.name}</td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            {k.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                <section className="bolum">
                  <h2 className="bolum-baslik">Saat dağılımı</h2>
                  {enYogunSaat && (
                    <p className="bolum-aciklama">
                      En yoğun saat: {String(enYogunSaat.hour).padStart(2, '0')}:00 —{' '}
                      {enYogunSaat.count} okuma
                    </p>
                  )}
                  <div className="saat-grafik">
                    {veri.byHour.map((s) => (
                      <div key={s.hour} className="saat-sutun" title={`${s.count} okuma`}>
                        <div
                          className="saat-dolgu"
                          style={{
                            height: `${(s.count / (enYogunSaat?.count || 1)) * 100}%`,
                          }}
                        />
                        <span className="saat-etiket">{s.hour}</span>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              {hatalilar && (
                <section className="bolum">
                  <h2 className="bolum-baslik">Hatalı eşleşmeler ({hatalilar.length})</h2>
                  <p className="bolum-aciklama">
                    {tenant.scanRules.a.label}, {tenant.scanRules.b.label} ile
                    beklenen ilişkiyi sağlamıyor. Satıra tıklayınca kodlar görünür.
                  </p>
                  <table className="kayit-tablo">
                    <thead>
                      <tr>
                        <th>{tenant.scanRules.a.label}</th>
                        <th>{tenant.scanRules.b.label}</th>
                        <th>Beklenen</th>
                        <th>Saat</th>
                        <th>Okutan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hatalilar.map((h) => (
                        <tr
                          key={h.id}
                          className="tiklanabilir"
                          onClick={() => setSecili(h)}
                        >
                          <td className="kod">{h.codeA}</td>
                          <td className="kod">{h.codeB}</td>
                          <td className="kod beklenen">
                            {tenant.matchRule.type === 'prefix'
                              ? tenant.matchRule.prefix + (h.codeB ?? '')
                              : h.codeB}
                          </td>
                          <td>
                            {new Date(h.createdAt).toLocaleTimeString('tr-TR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </td>
                          <td>{h.createdByName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )
        )}
      </main>

      {secili && (
        <QrDialog
          kodlar={[
            { etiket: tenant.scanRules.a.label, deger: secili.codeA ?? '' },
            { etiket: tenant.scanRules.b.label, deger: secili.codeB ?? '' },
          ].filter((k) => k.deger)}
          altNot={tenant.name}
          onClose={() => setSecili(null)}
        />
      )}
    </>
  );
}
